/**
 * @file: useThreadList.ts
 *
 * The thread-list data: subscribes the thread store, sorts pinned-first then
 * by most-recent activity, and exposes pull-to-refresh state. The page just
 * renders the result.
 */

import { useChatThreads } from '@euler/app/flows/chat/lib/threads/context';
import { ChatThreadFlag } from '@euler/app/flows/chat/lib/threads/types';
import { chatThreadIsPinned } from '@euler/app/flows/chat/utils/chatThreadDisplay';
import { ChatThreadItem } from '@euler/model/chat';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { useCallback, useMemo, useState } from 'react';

export interface ThreadList {
  sortedThreads: ChatThreadItem[];
  /** True until the first fetch resolves (drives the skeleton). */
  loading: boolean;
  refreshing: boolean;
  onRefresh: () => void;
  setThreadFlag: (threadId: string, flag: ChatThreadFlag, on: boolean) => void;
  deleteThread: (threadId: string) => void;
}

export function useThreadList(): ThreadList {
  const {
    chatThreads$,
    loading$,
    refreshChatThreads,
    setThreadFlag,
    deleteThread,
  } = useChatThreads();
  const [threads] = useBehaviorSubject(chatThreads$);
  const [loading] = useBehaviorSubject(loading$);
  const [refreshing, setRefreshing] = useState(false);

  // Pinned first, then most-recent activity. Contacts no longer live in
  // this list — they're in the header-launched contact panel.
  const sortedThreads = useMemo(() => {
    const sortFn = (a: ChatThreadItem, b: ChatThreadItem) =>
      new Date(b.latestActivityAt).getTime() -
      new Date(a.latestActivityAt).getTime();
    const pinned = threads.filter(chatThreadIsPinned).slice().sort(sortFn);
    const unpinned = threads
      .filter(t => !chatThreadIsPinned(t))
      .slice()
      .sort(sortFn);
    return [...pinned, ...unpinned];
  }, [threads]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await refreshChatThreads();
    setRefreshing(false);
  }, [refreshChatThreads]);

  return {
    sortedThreads,
    loading,
    refreshing,
    onRefresh,
    setThreadFlag,
    deleteThread,
  };
}
